import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from 'app/hooks';
import { DeleteDialog } from 'common/components/deleteDialog/DeleteDialog';
import { DeleteDialogCustomField } from 'common/components/deleteDialog/DeleteDialogCustomField';
import { setDialogOpen } from 'common/components/deleteDialog/deleteDialogSlice';
import { selectPageInfo, setRows } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { deleteUserGroupRoleMembership } from 'common/services/api/UserGroupRoleMembership/UserGroupRoleMembership.thunk';
import { DeleteDialogContentProps } from 'common/types/TypeDefs';
import { handleDeleteDialogError } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

export function UserGroupRoleMembershipDeleteDialog({
    title,
    itemId,
    editingRow
}: Readonly<DeleteDialogContentProps<{ userGroupName: string, roleId: string, userGroupId: string }>>) {
    const { userGroupName, roleId, userGroupId } = editingRow ?? {};
    const { enqueueSnackbar } = useSnackbar();
    const [processing, setProcessing] = useState<boolean>(false);

    const successDeleteHandler = (): void => {
        dispatch(setDialogOpen(false));
        enqueueSnackbar('User Group link has been deleted.', { variant: 'success' });
        setProcessing(false);
    };

    const handleDeleteActionError = (error: { message: string; }) => {
        handleDeleteDialogError('User Group link', enqueueSnackbar, extractStatusCode(error.message));
        setProcessing(false);
    };

    const dispatch = useAppDispatch();
    const params = useParams();
    const { tenantId } = params;
    const pageInfo = useAppSelector(selectPageInfo);

    const roleDeleteCallback = useCallback(() => {
        if (itemId) {
            setProcessing(true);
            dispatch(deleteUserGroupRoleMembership({
                tenantId: tenantId ?? '',
                roleId: roleId ?? '',
                userGroupId: userGroupId ?? ''
            }))
                .unwrap()
                .then((result) => {
                    dispatch(setRows(result.rows));
                    successDeleteHandler();
                })
                .catch((error) => {
                    handleDeleteActionError(error);
                });
        }
    }, [itemId]);

    return  (
        <DeleteDialog deleteCallback={roleDeleteCallback} processing={processing ?? false} title={`${title} link`}>
            <DeleteDialogCustomField fieldName='User Group Name' fieldValue={userGroupName}/>
            <DeleteDialogCustomField fieldName='Role Name' fieldValue={pageInfo?.name}/>
            <DeleteDialogCustomField fieldName='Tenant Name' fieldValue={pageInfo?.tenantName}/>
        </DeleteDialog>
    );
}
